import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import {
  type ChatMessage,
  type ChatMessagePart,
} from "../../../models/chat-message.ts";
import { type ReasoningUIPart } from "ai";
import * as Styles from "../../styles.ts";
import { defaultMarkdownStyles } from "../../styles.ts";
import { monotonicUlid } from "@std/ulid/monotonic-ulid";
import { compactOutputToMessage } from "../../../tools/compact/util.ts";
import {
  type Segment,
  groupPartsIntoSegments,
} from "../../../ai-sdk/thinking-parts.ts";
import { faviconUrl } from "../../util/favicon.ts";
import "../dui/markdown/dui-markdown.ts";
import "../dui/vstack/dui-vstack.ts";
import "../dui/collapsible/dui-collapsible.ts";
import "./tool-askUser.ts";
import "./canvas-view-card.ts";
import "./note-preview.ts";
import "../dui/link/dui-link.ts";
import "../dui/preview-card/dui-preview-card.ts";
import "../dui/preview-card/dui-preview-card-trigger.ts";
import "../dui/preview-card/dui-preview-card-popup.ts";
import "../dui/trunc/dui-trunc.ts";

// --- Note helpers ---

type NoteSummary = { title: string; type: string; content: string };

const parseNoteSummary = (output: string): NoteSummary | undefined => {
  try {
    const parsed = JSON.parse(output);
    if (parsed && typeof parsed.title === "string") return parsed;
    return undefined;
  } catch {
    return undefined;
  }
};

const isNoteUrl = (url: string): boolean =>
  url.startsWith("note://") || url.startsWith("lib://");

// --- Helpers ---

const lastNonEmptyLine = (text: string): string | undefined => {
  let end = text.length;
  while (end > 0) {
    const start = text.lastIndexOf("\n", end - 1);
    const line = text.slice(start + 1, end).trim();
    if (line) return line;
    end = start;
  }
  return undefined;
};

// --- Labels & active detection ---

const MAX_SEARCH_RESULTS = 5;

// --- Per-type row icon + content ---

const WebSearchResultsView = (
  results: { url: string; title: string | null }[],
): TemplateResult => {
  const visible = results.slice(0, MAX_SEARCH_RESULTS);
  const overflow = results.length - visible.length;
  return html`
    <div class="search-results">
      ${visible.map(
        (r) => html`
          <div class="search-result">
            <img class="favicon" src="${faviconUrl(r.url)}" alt="" />
            <a
              class="search-result-title"
              href="${r.url}"
              target="_blank"
              rel="noopener noreferrer"
              >${r.title ?? r.url}</a
            >
          </div>
        `,
      )}
      ${overflow > 0
        ? html`<div class="search-result search-result-overflow">
            +${overflow} more
          </div>`
        : nothing}
    </div>
  `;
};

type ThinkingRowParts = {
  icon: string;
  content: TemplateResult;
};

const thinkingRowParts = (part: ChatMessagePart): ThinkingRowParts => {
  switch (part.type) {
    case "reasoning": {
      const r = part as ReasoningUIPart;
      const summary = lastNonEmptyLine(r.text ?? "");

      if (!summary) {
        return {
          icon: "psychology",
          content: html`${r.state === "done" ? "Thought" : "Thinking..."}`,
        };
      }

      return {
        icon: "psychology",
        content: html`<dui-trunc
          class="reasoning-summary"
          max-lines="2"
          max-width="100%"
          >${summary}</dui-trunc
        >`,
      };
    }
    case "tool-replaceMemory": {
      if (!part.output) {
        return { icon: "neurology", content: html`Updating memory...` };
      }

      const output = part.output;
      if (!output.ok) {
        return {
          icon: "neurology",
          content: html`Memory error: ${output.error}`,
        };
      }

      const suffix = output.value.truncated ? " (truncated)" : "";
      const key = output.value.key;
      const action = output.value.action;
      const newContent: string | undefined = part.input?.newContent;

      if (!newContent) {
        return {
          icon: "neurology",
          content: html`Memory ${action}${suffix}: <strong>${key}</strong>`,
        };
      }

      return {
        icon: "neurology",
        content: html`
          Memory ${action}${suffix}:
          <dui-preview-card>
            <dui-preview-card-trigger>
              <span class="preview-trigger">${key}</span>
            </dui-preview-card-trigger>
            <dui-preview-card-popup>
              <note-preview .value="${newContent}"></note-preview>
            </dui-preview-card-popup>
          </dui-preview-card>
        `,
      };
    }
    case "tool-fetch": {
      const url: string | undefined = part.input?.url;
      if (!url) {
        return { icon: "progress_activity", content: html`Fetching...` };
      }

      // Non-note URLs: keep as clickable link
      if (!isNoteUrl(url)) {
        return {
          icon: "language",
          content: html`Fetched: <dui-link href="${url}">${url}</dui-link>`,
        };
      }

      // Note URLs: show preview card if output is parsed
      const note = part.output ? parseNoteSummary(part.output) : undefined;
      if (!note) return { icon: "draft", content: html`${url}` };

      const noteKind = url.startsWith("lib://") ? "library" : "project";
      return {
        icon: "draft",
        content: html`
          Fetched ${noteKind} note:
          <dui-preview-card>
            <dui-preview-card-trigger>
              <span class="preview-trigger"
                >${url.replace(/^(note|lib):\/\//, "")}</span
              >
            </dui-preview-card-trigger>
            <dui-preview-card-popup>
              <note-preview
                .heading="${note.title}"
                .value="${note.content}"
              ></note-preview>
            </dui-preview-card-popup>
          </dui-preview-card>
        `,
      };
    }
    case "tool-webSearch": {
      const query = part.input?.query;
      return {
        icon: "language",
        content: part.output
          ? html`Searched web: ${query}${WebSearchResultsView(part.output)}`
          : html`${query ?? "Searching the web..."}`,
      };
    }
    case "tool-searchNotes": {
      const results = part.output?.length;
      return {
        icon: "search",
        content: html`${results
          ? `Searched notes: ${results} results found`
          : "Searching notes..."}`,
      };
    }
    case "tool-saveNote": {
      const note = part.input?.note;
      const title = note?.title;
      if (!part.output) {
        return { icon: "edit_note", content: html`Saving note...` };
      }
      return {
        icon: "edit_note",
        content: html`
          Saved note:
          <dui-preview-card>
            <dui-preview-card-trigger>
              <span class="preview-trigger">${title}</span>
            </dui-preview-card-trigger>
            <dui-preview-card-popup>
              <note-preview
                .heading="${title}"
                .value="${note?.content ?? ""}"
              ></note-preview>
            </dui-preview-card-popup>
          </dui-preview-card>
        `,
      };
    }
    case "tool-compact":
      return {
        icon: "compress",
        content: html`${part.output
          ? compactOutputToMessage(part.output)
          : "Compacting..."}`,
      };
    default:
      return { icon: "circle", content: html`${part.type}` };
  }
};

const ThinkingRowView = (part: ChatMessagePart): TemplateResult => {
  const { icon, content } = thinkingRowParts(part);
  return html`
    <dui-icon class="thinking-row-icon" icon="${icon}"></dui-icon>
    <div class="thinking-row-content">${content}</div>
  `;
};

/** Find the last part that represents a substantive action (not a structural marker). */
const lastSubstantivePart = (
  parts: ChatMessagePart[],
): ChatMessagePart | undefined =>
  parts.findLast((p) => p.type !== "step-start");

const isThinkingGroupActive = (parts: ChatMessagePart[]): boolean => {
  const last = lastSubstantivePart(parts);
  if (!last) return false;
  if (last.type === "reasoning") {
    return (last as ReasoningUIPart).state !== "done";
  }
  // Tool parts are "active" if they have no output yet
  if ("output" in last) return last.output === undefined;
  return false;
};

const emptyMessage = ({
  id = monotonicUlid(),
  role = "user",
  parts = [],
}: Partial<ChatMessage> = {}): ChatMessage => ({
  id,
  role,
  parts,
  metadata: undefined,
});

// --- Views ---

const TextPartView = (text: string): TemplateResult => {
  return html`
    <dui-markdown value="${text}" .contentStyles=${chatStyles}></dui-markdown>
  `;
};

const ACTIVE_PART_LABELS: Record<string, string> = {
  reasoning: "Thinking",
  "tool-webSearch": "Searching the web",
  "tool-fetch": "Fetching",
  "tool-searchNotes": "Searching notes",
  "tool-saveNote": "Saving note",
  "tool-compact": "Compacting",
  "tool-replaceMemory": "Updating memory",
};

const activeThinkingTitle = (parts: ChatMessagePart[]): string => {
  const last = lastSubstantivePart(parts);
  if (!last) return "Thinking...";
  return (ACTIVE_PART_LABELS[last.type] ?? "Working") + "...";
};

const ThinkingGroupView = (
  parts: ChatMessagePart[],
  summary?: string,
): TemplateResult | typeof nothing => {
  const visibleParts = parts.filter((p) => p.type !== "step-start");
  if (visibleParts.length === 0) return nothing;
  const active = isThinkingGroupActive(parts);
  const title = active
    ? activeThinkingTitle(parts)
    : summary || activeThinkingTitle(parts);
  return html`
    <dui-collapsible class="thinking-group">
      <dui-trunc
        slot="title"
        class="thinking-group-title"
        max-lines="1"
        max-width="100%"
        >${title}</dui-trunc
      >
      <div slot="content" class="thinking-rows">
        ${visibleParts.map(
          (part) =>
            html`<div class="thinking-row">${ThinkingRowView(part)}</div>`,
        )}
      </div>
    </dui-collapsible>
  `;
};

const SinglePartView = (
  part: ChatMessagePart,
  latestToolCallId: string | undefined,
): TemplateResult | typeof nothing => {
  switch (part.type) {
    case "text":
      return TextPartView(part.text);
    case "tool-askUser": {
      if (!part.output?.length) return nothing;
      const isLatest =
        "toolCallId" in part && part.toolCallId === latestToolCallId;
      return html`
        <tool-ask-user
          .questions="${part.output}"
          ?disabled="${!isLatest}"
        ></tool-ask-user>
      `;
    }
    case "tool-askUserCards": {
      if (!part.output?.length) return nothing;
      const isLatest =
        "toolCallId" in part && part.toolCallId === latestToolCallId;
      return html`
        <tool-ask-user
          .questions="${part.output}"
          display="card"
          ?disabled="${!isLatest}"
        ></tool-ask-user>
      `;
    }
    case "tool-activateCanvasView": {
      const name = part.output?.name ?? part.input?.id ?? "View";
      const view = part.input?.id ?? "";
      return html`
        <canvas-view-card .view="${view}" .name="${name}"></canvas-view-card>
      `;
    }
    default:
      return nothing;
  }
};

const SegmentView = (
  segment: Segment,
  latestToolCallId: string | undefined,
  summary?: string,
): TemplateResult | typeof nothing => {
  if (segment.kind === "thinking") {
    return ThinkingGroupView(segment.parts, summary);
  }
  return SinglePartView(segment.part, latestToolCallId);
};

const MessageView = (
  message: ChatMessage,
  latestToolCallId: string | undefined,
  thinkingSummaries: string[] = [],
): TemplateResult => {
  const segments = groupPartsIntoSegments(message.parts);
  let thinkingIndex = 0;
  const content = html`
    ${segments.map((seg) => {
      const summary =
        seg.kind === "thinking"
          ? thinkingSummaries[thinkingIndex++]
          : undefined;
      return SegmentView(seg, latestToolCallId, summary);
    })}
  `;

  if (message.role === "user") {
    return html`
      <div class="user-message">
        <div class="user-message-bubble">${content}</div>
      </div>
    `;
  }

  const className =
    message.role === "assistant" ? "assistant-message" : "system-message";
  return html`<div class="${className}">${content}</div>`;
};

// --- Styles ---

const styles = css`
  :host {
    display: block;
  }

  .user-message {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin: var(--extra-margin-for-bordered-messages) 0;
  }

  .user-message-bubble {
    background-color: var(--secondary);
    border-radius: var(--radius-md);
    padding: var(--space-4);
  }

  .assistant-message {
    display: flex;
    flex-direction: column;
    gap: var(--paragraph-spacing);
  }

  .system-message {
    display: flex;
  }

  dui-collapsible {
    --trigger-height: fit-content;
  }

  dui-collapsible.thinking-group::part(root) {
    border: none;
  }

  dui-collapsible.thinking-group::part(trigger) {
    padding: 0;
    justify-content: start;
    gap: var(--space-0_5);
  }

  dui-collapsible.thinking-group::part(content) {
    padding: var(--space-0_5) 0 0;
  }

  dui-collapsible.thinking-group::part(icon) {
    opacity: 0.4;
  }

  .thinking-rows {
    display: flex;
    flex-direction: column;
    margin-left: 0.4rem;
  }

  .thinking-group-title {
    color: var(--muted-foreground);
    transition-property: color;
    transition-duration: var(--duration-fast);
    transition-timing-function: var(--ease-out-3);
  }

  .thinking-group-title:hover {
    color: var(--foreground);
  }

  .thinking-row {
    position: relative;
    display: flex;
    align-items: flex-start;
    font-size: var(--font-size-sm);
    line-height: var(--line-height-snug);
    color: var(--muted-foreground);
    border-left: 2px solid var(--border);
    padding: var(--space-0_5) 0 var(--space-0_5) var(--space-3);
  }

  .thinking-row-icon {
    position: absolute;
    left: -0.08rem;
    top: 0.15rem;
    transform: translateX(-50%);
    --icon-size: var(--space-4);
    --icon-fg: var(--muted-foreground);
    background: var(--background);
    outline: var(--border-width-medium) solid var(--background);
    border-radius: var(--radius-full);
  }

  .thinking-row-content {
    width: 100%;
    color: var(--muted-foreground);
  }

  .thinking-row-content .preview-trigger {
    color: var(--secondary-foreground);
    text-decoration: underline 1px solid
      color-mix(in oklch, var(--muted-foreground) 60%, oklch(0 0 0 / 0));
    text-underline-offset: 1.5px;
  }

  .reasoning-summary {
    color: var(--muted-foreground);
  }

  .search-results {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    margin-top: var(--space-1);
  }

  .search-result {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
  }

  a.search-result-title,
  a.search-result-title:visited,
  a.search-result-title:hover {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--secondary-foreground);
    text-decoration: underline 1px solid
      color-mix(in oklch, var(--muted-foreground) 60%, oklch(0 0 0 / 0));
    text-underline-offset: 1.5px;
  }

  .search-result-overflow {
    color: var(--muted-foreground);
  }

  .favicon {
    width: var(--space-3);
    height: var(--space-3);
    flex-shrink: 0;
    border-radius: 2px;
  }

  & > * + * {
    margin-top: var(--paragraph-spacing);
  }
`;

const chatMarkdownOverrides = css`
  .content {
    --default-margin-top: var(--paragraph-spacing);

    & > p {
      font-size: var(--font-size-base);
      font-weight: var(--font-weight-regular);
      letter-spacing: var(--letter-spacing-tight);
      line-height: var(--line-height-snug);
      color: var(--plain-text-color);
    }
  }
`;

/** Hoisted so Lit sees the same reference on every render. */
const chatStyles = [defaultMarkdownStyles, chatMarkdownOverrides];

// --- Element ---

@customElement("chat-message")
export class ChatMessageElement extends LitElement {
  static override styles = [Styles.base, styles];

  @property({ type: Object })
  accessor state: ChatMessage = emptyMessage();

  @property({ type: String })
  accessor latestToolCallId: string | undefined = undefined;

  @property({ attribute: false })
  accessor thinkingSummaries: string[] = [];

  override render(): TemplateResult {
    return MessageView(
      this.state,
      this.latestToolCallId,
      this.thinkingSummaries,
    );
  }
}
